import {
  ref,
  shallowRef,
  nextTick,
  defineAsyncComponent,
  type AsyncComponentLoader,
  type Component
} from 'vue'

import ComponentLoader from '@/components/shared/component-loader.vue'
import ComponentError from '@/components/shared/component-error.vue'

export interface DynamicComponent {
  component: Component
  props: object
  events: Events
}

type DynamicComponentLoader = AsyncComponentLoader<Component | Promise<Component>>
type Events = {
  [key: string]: () => void
}

export const useDynamicComponent = () => {
  const visible = ref(false)
  const props = ref({})
  const content = shallowRef<DynamicComponent>()
  let timeout: ReturnType<typeof setTimeout> | undefined

  const open = async (component: Component | DynamicComponentLoader, props: object = {}, events: Events = {}) => {
    if (timeout) {
      return
    }
    if (visible.value) {
      await close()
    }

    if (typeof component === 'function') {
      component = defineAsyncComponent({
        loader: component as DynamicComponentLoader,
        loadingComponent: ComponentLoader,
        errorComponent: ComponentError
      })
    }

    content.value = {
      component,
      props,
      events
    }
    await nextTick()
    visible.value = true
  }

  const close = async () => {
    return new Promise<void>((resolve) => {
      if (typeof content.value?.events?.close === 'function') {
        content.value?.events?.close()
      }
      visible.value = false
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        timeout = content.value = undefined
        resolve()
      }, 200)
    })
  }

  return {
    props,
    visible,
    content,
    open,
    close
  }
}

export type DynamicContentAPI = ReturnType<typeof useDynamicComponent>
